import {EditorSelection} from '@codemirror/state'
import type {Command, EditorView} from '@codemirror/view'
import type {Nodes} from 'mdast'

import {getNodesInsideRange} from '../../markdown/node-range-utils'
import {skipBlockquoteContinuation} from '../extensions/markdown-behaviors'
import {getMarkdownTree} from '../extensions/markdown-parser'
import {createText} from '../extensions/single-line-editor'

/**
 * Check if this line is a blank line inside a blockquote. `lineFrom` should be the index of the first character of
 * the line, while `lineTo` should be the index of the newline that ends the line.
 */
function isBlankBlockquoteLine(tree: Nodes, lineFrom: number, lineTo: number) {
  const lineQuotePrefixes = Array.from(getNodesInsideRange(tree, lineFrom, lineTo)).filter(
    n => n.type === 'delimiter' && n.tokenType === 'blockQuotePrefix',
  )
  return lineQuotePrefixes.length > 0 && lineQuotePrefixes.at(-1)?.position?.end.offset === lineTo
}

/**
 * Dispatch an undoable transaction to split the current blockquote apart, if the caret is on a blank line inside
 * of a blockquote of any depth. This operation only applies for a single caret with no text selected.
 */
export const splitBlockquoteIfBlankLine: Command = (view: EditorView) => {
  if (view.state.selection.ranges.length !== 1 || !view.state.selection.main.empty) return false

  const caretLocation = view.state.selection.main.anchor
  const document = view.state.doc.toString()

  const lineFrom = document.slice(0, caretLocation).lastIndexOf('\n') + 1
  let lineTo = document.indexOf('\n', caretLocation)
  if (lineTo === -1) lineTo = document.length

  const tree = getMarkdownTree(view.state)

  if (isBlankBlockquoteLine(tree, lineFrom, lineTo)) {
    view.dispatch({
      changes: [
        {
          from: lineFrom,
          to: lineTo,
          insert: createText('\n\n'),
        },
      ],
      // Update the selection to fall on the middle line
      selection: EditorSelection.single(lineFrom + 1),
      annotations: [skipBlockquoteContinuation.of(true)],
      userEvent: 'input',
      scrollIntoView: true,
    })
    return true // handled
  }

  return false
}
